import { forwardRef, useState } from 'react';
import { useT } from '../context/LocaleContext';
import { setPartsRequestStatus } from '../lib/bookingData';
import { formatWhen } from '../lib/format';
import { supabase } from '../lib/supabase';
import { Icon } from './JobUI';

/**
 * Parts and quality-check requests the mechanic raised from the app.
 * The desk approves, orders, rejects or signs them off here.
 */
const MechanicRequests = forwardRef(function MechanicRequests(
  { parts, qc, canReview, userId, onChanged },
  ref,
) {
  const t = useT();
  const [busyId, setBusyId] = useState('');
  const [error, setError] = useState('');

  if (!parts.length && !qc.length) return null;

  async function run(id, task) {
    setBusyId(id);
    setError('');
    const result = await task();
    setBusyId('');
    if (result?.error) {
      setError(result.error.message);
      return;
    }
    onChanged?.();
  }

  const reviewQc = (id, approved) =>
    run(id, () =>
      supabase.rpc('review_technician_qc', {
        p_qc_request_id: id,
        p_approved: approved,
        p_reviewer_notes: approved ? t('qc.approvedNote') : t('qc.needsWork'),
      }),
    );

  const setParts = (id, status) => run(id, () => setPartsRequestStatus(id, status, userId));
  const pendingCount =
    parts.filter((row) => row.status === 'pending').length + qc.filter((row) => row.status === 'pending').length;

  return (
    <section ref={ref} className={`requests-card${pendingCount ? ' has-pending' : ''}`}>
      <header>
        <Icon name="engineering" />
        <h2>{t('req.title')}</h2>
        {pendingCount ? <em>{pendingCount}</em> : null}
      </header>
      {error ? <p className="form-error">{error}</p> : null}
      <ul>
        {qc.map((row) => (
          <li key={row.id} className={`req req-${row.status}`}>
            <span className="req-kind">
              <Icon name="fact_check" />
              {t('req.qc')}
            </span>
            <span className="req-main">
              <strong>{t('req.qcRequested', { when: formatWhen(row.requested_at) })}</strong>
              {row.notes ? <small>{row.notes}</small> : null}
              {row.reviewer_notes && row.status !== 'pending' ? <small>{row.reviewer_notes}</small> : null}
            </span>
            <span className={`req-status req-status-${row.status}`}>
              {row.status === 'approved' ? t('req.qcSigned') : t(`req.status.${row.status}`)}
            </span>
            {row.status === 'pending' && canReview ? (
              <span className="req-actions">
                <button type="button" className="quick-btn" disabled={busyId === row.id} onClick={() => reviewQc(row.id, true)}>
                  <Icon name="verified" />
                  {t('req.qcApprove')}
                </button>
                <button type="button" className="ghost danger" disabled={busyId === row.id} onClick={() => reviewQc(row.id, false)}>
                  {t('req.qcReject')}
                </button>
              </span>
            ) : null}
          </li>
        ))}
        {parts.map((row) => (
          <li key={row.id} className={`req req-${row.status}`}>
            <span className="req-kind">
              <Icon name="inventory_2" />
              {t('req.parts')}
            </span>
            <span className="req-main">
              <strong>
                {row.part_name}
                {row.part_number ? <span className="mono"> · {row.part_number}</span> : null}
              </strong>
              <small>
                {t('req.qty', { n: row.quantity })} · {formatWhen(row.created_at)}
                {row.urgency && row.urgency !== 'normal' ? (
                  <b className={`req-urgency req-urgency-${row.urgency}`}> · {t(`req.urgency.${row.urgency}`)}</b>
                ) : null}
              </small>
              {row.notes ? <small>{row.notes}</small> : null}
            </span>
            <span className={`req-status req-status-${row.status}`}>{t(`req.status.${row.status}`)}</span>
            {canReview && (row.status === 'pending' || row.status === 'approved') ? (
              <span className="req-actions">
                {row.status === 'pending' ? (
                  <>
                    <button type="button" className="quick-btn" disabled={busyId === row.id} onClick={() => setParts(row.id, 'approved')}>
                      <Icon name="shopping_cart" />
                      {t('req.approve')}
                    </button>
                    <button type="button" className="ghost danger" disabled={busyId === row.id} onClick={() => setParts(row.id, 'rejected')}>
                      {t('req.reject')}
                    </button>
                  </>
                ) : (
                  <button type="button" className="quick-btn" disabled={busyId === row.id} onClick={() => setParts(row.id, 'fulfilled')}>
                    <Icon name="inventory" />
                    {t('req.arrived')}
                  </button>
                )}
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
});

export default MechanicRequests;
